import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter, map, startWith } from 'rxjs';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { resolveActiveNavItem } from '../../core/config/admin-nav';

/**
 * Placeholder page for the Phase 2 shell. Every rail destination resolves here
 * until its real page is migrated in Phase 3. It names the area (from the rail
 * model) so the shell reads as a working app while the pages are ported.
 */
@Component({
  selector: 'ws-placeholder-page',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslocoPipe],
  template: `
    <div class="placeholder">
      <h1>{{ label() }}</h1>
      <p>{{ 'placeholder.body' | transloco }}</p>
    </div>
  `,
  styles: [
    `
      .placeholder {
        max-width: 60ch;
      }
      h1 {
        margin: 0 0 8px;
        font-family: var(--font-display, var(--font-sans));
        font-size: 19px;
        font-weight: 600;
        letter-spacing: -0.012em;
        color: var(--text);
      }
      p {
        margin: 0;
        font-size: 13px;
        color: var(--text-secondary);
      }
    `,
  ],
})
export class PlaceholderPageComponent {
  private readonly router = inject(Router);
  private readonly transloco = inject(TranslocoService);

  private readonly url = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map((e) => e.urlAfterRedirects),
      startWith(this.router.url),
    ),
    { initialValue: this.router.url },
  );

  private readonly lang = toSignal(this.transloco.langChanges$, {
    initialValue: this.transloco.getActiveLang(),
  });

  protected readonly label = computed(() => {
    this.lang();
    return resolveActiveNavItem(this.url())?.label ?? this.transloco.translate('placeholder.fallback_title');
  });
}
